<template>
  <div
    class="cb-page cb-page--fixed critical-screen"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="critical-title"
    aria-describedby="critical-message"
  >
    <span class="critical-screen__bg" aria-hidden="true"></span>

    <div class="critical-screen__center">
      <GlowRing class="critical-screen__ring" :size="ringSize" :rings="3" arc spin pulse intensity="strong">
        <span class="critical-screen__icon">
          <AppIcon name="warning" :size="46" :stroke-width="1.9" />
        </span>
      </GlowRing>
      <h1 id="critical-title" class="critical-screen__title">{{ title }}</h1>
      <p id="critical-message" class="critical-screen__message">{{ message }}</p>
    </div>

    <div class="cb-page__footer critical-screen__actions">
      <AppButton icon="refresh" block :loading="reloading" @click="reconnect">{{ actionLabel }}</AppButton>
      <AppButton variant="secondary" size="md" icon="close" block class="critical-screen__close" :disabled="reloading" @click="closeApp">
        Закрыть
      </AppButton>
    </div>
  </div>
</template>

<script setup>
/**
 * CriticalScreen (/critical-modal) — фатальные ошибки: сессия истекла, вход с другого устройства,
 * нет соединения с сервером. Открывается только через openCritical() (services/critical.js);
 * без активного состояния guard маршрута отправляет на «/».
 *
 * «Переподключиться» / «Перезайти» — полная перезагрузка приложения (заново авторизуется через Telegram).
 * «Закрыть» — Telegram.WebApp.close(); вне Telegram закрыть вкладку нельзя — тоже перезагрузка.
 * Системная «Назад» Telegram здесь тоже перезагружает приложение: «мягкий» возврат на «/» оставил бы
 * его без соединения (WebSocket закрыт окончательно, токен мог истечь).
 */
import { computed, onMounted, ref } from "vue";
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import GlowRing from "@/components/ui/GlowRing.vue";
import telegram from "@/services/telegram";
import { CRITICAL, criticalState, runCriticalAction } from "@/services/critical";

const reloading = ref(false);
const ringSize = Math.round(Math.min(220, Math.max(170, window.innerHeight * 0.26)));

const title = computed(() => criticalState.title || CRITICAL.GENERIC.title);
const message = computed(() => criticalState.message || CRITICAL.GENERIC.message);
const actionLabel = computed(() => criticalState.actionLabel || CRITICAL.GENERIC.actionLabel);

function reconnect() {
  if (reloading.value) return;
  reloading.value = true;
  telegram.haptic("medium");
  runCriticalAction("reload");
}

function closeApp() {
  if (reloading.value) return;
  if (!telegram.isTelegram) reloading.value = true;
  runCriticalAction("close");
}

onMounted(() => {
  telegram.haptic("error");
  // afterEach роутера уже показал «Назад» с переходом на «/» — заменяем обработчик.
  telegram.backButton(true, reconnect);
});
</script>

<style scoped>
.critical-screen {
  justify-content: center;
  overflow: hidden;
  isolation: isolate;
}

.critical-screen__bg {
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background:
    radial-gradient(60% 40% at 50% 36%, rgba(221, 245, 90, 0.1) 0%, rgba(221, 245, 90, 0) 70%),
    radial-gradient(80% 50% at 50% 100%, rgba(255, 107, 90, 0.06) 0%, rgba(255, 107, 90, 0) 70%);
}

.critical-screen__center {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px 4px;
  text-align: center;
}

.critical-screen__ring {
  margin-bottom: 8px;
}

.critical-screen__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 50% 35%, rgba(221, 245, 90, 0.16) 0%, rgba(221, 245, 90, 0) 70%),
    var(--cb-surface-2);
  border: 1px solid var(--cb-lime);
  color: var(--cb-lime);
  box-shadow: var(--cb-glow), inset 0 0 18px rgba(221, 245, 90, 0.12);
}

.critical-screen__icon .app-icon {
  filter: drop-shadow(0 0 8px rgba(221, 245, 90, 0.6));
}

.critical-screen__title {
  max-width: 340px;
  font-size: clamp(24px, 7.4vw, 30px);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.critical-screen__message {
  max-width: 330px;
  font-size: 16px;
  line-height: 1.5;
  color: var(--cb-text-2);
}

.critical-screen__actions {
  gap: 12px;
}

.critical-screen__actions .critical-screen__close {
  height: 56px;
  font-size: 18px;
}
</style>
